import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService } from '@services/api-client';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { AgreementStatus } from 'app/types';
import { ReplaySubject, share, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class CachedValuesService {
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private invalidateCacheSubject = new Subject<void>();
    private invalidateCache$ = this.invalidateCacheSubject.asObservable();

    // private cachedData: SubscriptionResponse | null = null;
    // private fetchPromise: Promise<SubscriptionResponse> | null = null;

    // public async getSubscription(): Promise<SubscriptionResponse> {
    //     if (this.cachedData) {
    //         return this.cachedData;
    //     }

    //     // If fetch is in progress, wait for it
    //     if (this.fetchPromise) {
    //         return this.fetchPromise;
    //     }

    //     // Otherwise, fetch and cache it
    //     this.fetchPromise = firstValueFrom(this.apiClientService.getSubscription$());

    //     try {
    //         this.cachedData = await this.fetchPromise;
    //         return this.cachedData;
    //     } finally {
    //         this.fetchPromise = null; // clear after fetch completes
    //     }
    // }

    public customers$ = this.apiClientService.getCustomers$().pipe(
        share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () => this.invalidateCache$
        })
    );

    public users$ = this.apiClientService.getUsers$().pipe(
        share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () => this.invalidateCache$
        })
    );

    public counterparts$ = this.apiClientService.getCounterparts$().pipe(
        share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () => this.invalidateCache$
        })
    );

    // public areas$ = this.apiClientService.getTypeListItems$('AREA').pipe(
    //     share({
    //         connector: () => new ReplaySubject(1),
    //         resetOnComplete: () => this.invalidateCache$
    //     })
    // );

    // public categories$ = this.apiClientService.getTypeListItems$('CATEGORY').pipe(
    //     share({
    //         connector: () => new ReplaySubject(1),
    //         resetOnComplete: () => this.invalidateCache$
    //     })
    // );

    public commitmentperiodtypes$ = this.apiClientService.getCommitmentPeriodTypes$().pipe(
        share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () => this.invalidateCache$
        })
    );

    public agreementTypes$ = this.apiClientService.getAgreementTypes$().pipe(
        share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () => this.invalidateCache$
        })
    );

    public currencies$ = this.apiClientService.getTypeListItems$('CURRENCY').pipe(
        share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () => this.invalidateCache$
        })
    );

    public subscription$ = this.apiClientService.getSubscription$().pipe(
        share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () => this.invalidateCache$
        })
    );

    public generateNumericDropdownItems(to: number, from: number = 0): DropdownItem<number>[] {
        const items: DropdownItem<number>[] = [];
        for (let i = from; i <= to; i++) {
            items.push({
                value: i,
                label: `${i}`
            });
        }
        return items;
    }

    public getPaymentTermsOptions(): DropdownItem<number>[] {
        const customValue = this.translateService.instant('PLACEHOLDERS.CUSTOM_VALUE') as string;
        const items: DropdownItem<number>[] = [
            { value: 10, label: `${10}` },
            { value: 15, label: `${15}` },
            { value: 20, label: `${20}` },
            { value: 30, label: `${30}` },
            { value: 45, label: `${45}` },
            { value: 60, label: `${60}` },
            { value: 90, label: `${90}` },
            { value: -1, label: customValue }
        ];

        return items;
    }

    public getInvoiceIntervalsOptions(): DropdownItem<number>[] {
        const items: DropdownItem<number>[] = [];
        for (let i = 0; i <= 6; i++) {
            items.push({
                value: i,
                label: this.translateService.instant('FEATURES.AGREEMENTS.INVOICE_INTERVALS.' + i) as string
            });
        }
        return items;
    }

    public getEndsInMonthsOptions(): DropdownItem<number>[] {
        const months = this.translateService.instant('GENERAL.MONTHS') as string;
        const items: DropdownItem<number>[] = [{ value: -1, label: `-` }];

        for (let i = 1; i <= 12; i++) {
            items.push({
                value: i,
                label: `${i}` + ' ' + months
            });
        }

        return items;
    }

    public getAgreementStatusOptions(): MultiSelectItem[] {
        const items: MultiSelectItem[] = [
            {
                id: AgreementStatus.Active,
                name: this.translateService.instant('FILTER.AGREEMENTSTATUS.' + AgreementStatus.Active) as string
            },
            {
                id: AgreementStatus.ReminderWarning,
                name: this.translateService.instant(
                    'FILTER.AGREEMENTSTATUS.' + AgreementStatus.ReminderWarning
                ) as string
            },
            {
                id: AgreementStatus.Expired,
                name: this.translateService.instant('FILTER.AGREEMENTSTATUS.' + AgreementStatus.Expired) as string
            },
            {
                id: AgreementStatus.Archived,
                name: this.translateService.instant('FILTER.AGREEMENTSTATUS.' + AgreementStatus.Archived) as string
            },
            {
                id: AgreementStatus.OnGoing,
                name: this.translateService.instant('FILTER.AGREEMENTSTATUS.' + AgreementStatus.OnGoing) as string
            }
        ];

        return items;
    }

    public getListTypeOptions(): DropdownItem<string>[] {
        const items: DropdownItem<string>[] = [
            {
                value: 'AREA',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.AREA') as string
            },
            {
                value: 'CATEGORY',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.CATEGORY') as string
            },
            {
                value: 'INVENTORY_USERS',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.INVENTORY_USERS') as string
            },
            {
                value: 'LOCATION',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.LOCATION') as string
            },
            {
                value: 'MANUFACTURER',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.MANUFACTURER') as string
            },
            {
                value: 'MODEL',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.MODEL') as string
            },
            {
                value: 'TYPE_OF_INVENTORY',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.TYPE_OF_INVENTORY') as string
            },
            {
                value: 'CAR_BRAND',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.CAR_BRAND') as string
            },
            {
                value: 'CAR_MODEL',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.CAR_MODEL') as string
            },
            {
                value: 'TYPE_OF_PREMISES',
                label: this.translateService.instant('FEATURES.LISTTYPE.TYPES.TYPE_OF_PREMISES') as string
            }
        ];

        return items;
    }

    public getAgreementTypeName(typeId: number) {
        return this.agreementTypes$.subscribe((response) => {
            return response.find((p) => p.name == typeId.toString());
        });
    }

    public invalidateCache() {
        this.invalidateCacheSubject.next();
        //this.init();
    }
}
